import { isRequestAborted } from '@core/api.js';

/**
 * Détails structurés d’une erreur Axios / API (RFC 7807 Problem, Symfony, etc.).
 * @param {unknown} err
 * @returns {{
 *   message: string,
 *   status: number | null,
 *   code: string | null,
 *   isNetwork: boolean,
 *   isAborted: boolean,
 *   isRateLimited: boolean,
 * }}
 */
export function parseApiError(err, fallback = 'Une erreur est survenue.') {
  const isAborted = isRequestAborted(err);
  if (isAborted) {
    return {
      message: fallback,
      status: null,
      code: 'aborted',
      isNetwork: false,
      isAborted: true,
      isRateLimited: false,
    };
  }

  const retryAfter = Number(err?.retryAfter ?? err?.response?.headers?.['retry-after'] ?? 0);
  const isRateLimited = Number.isFinite(retryAfter) && retryAfter > 0;

  if (isRateLimited) {
    return {
      message: `Trop de requêtes. Réessayez dans ${retryAfter} seconde(s).`,
      status: err?.response?.status ?? 429,
      code: 'rate_limit',
      isNetwork: false,
      isAborted: false,
      isRateLimited: true,
    };
  }

  const response = err?.response;
  if (!response) {
    const code = err?.code;
    const isTimeout = code === 'ECONNABORTED' || String(err?.message || '').toLowerCase().includes('timeout');
    return {
      message: isTimeout
        ? 'La requête a expiré. Vérifiez votre connexion et réessayez.'
        : 'Réseau indisponible ou serveur injoignable. Réessayez dans un instant.',
      status: null,
      code: code ? String(code) : 'network',
      isNetwork: true,
      isAborted: false,
      isRateLimited: false,
    };
  }

  const d = response.data;
  const status = typeof response.status === 'number' ? response.status : null;

  if (!d || typeof d !== 'object') {
    const raw = err?.message && typeof err.message === 'string' ? err.message : fallback;
    return {
      message: raw,
      status,
      code: null,
      isNetwork: false,
      isAborted: false,
      isRateLimited: false,
    };
  }

  const code = typeof d.code === 'string' ? d.code : (typeof d.type === 'string' ? d.type : null);

  const title = typeof d.title === 'string' && d.title.trim() !== '' ? d.title.trim() : null;
  const detail = typeof d.detail === 'string' && d.detail.trim() !== '' ? d.detail.trim() : null;
  const singleError = typeof d.error === 'string' && d.error.trim() !== '' ? d.error.trim() : null;
  const reason = typeof d.reason === 'string' && d.reason.trim() !== '' ? d.reason.trim() : null;
  const messageField = typeof d.message === 'string' && d.message.trim() !== '' ? d.message.trim() : null;

  /** @type {unknown} */
  const violations = d.violations ?? d.errors;
  let violationSummary = null;
  if (Array.isArray(violations) && violations.length > 0) {
    const parts = violations
      .slice(0, 5)
      .map((v) => {
        if (v && typeof v === 'object') {
          const p = /** @type {{ propertyPath?: string, message?: string, title?: string }} */ (v);
          const path = p.propertyPath || p.title || '';
          const msg = p.message || '';
          return path ? `${path}: ${msg}` : msg;
        }
        return String(v);
      })
      .filter(Boolean);
    if (parts.length) violationSummary = parts.join(' · ');
  }

  let message = title || singleError || reason || messageField || detail || violationSummary || fallback;
  if (title && detail && title !== detail && !messageField) {
    message = `${title} — ${detail}`;
  } else if (title && violationSummary) {
    message = `${title} — ${violationSummary}`;
  }

  return {
    message,
    status,
    code,
    isNetwork: false,
    isAborted: false,
    isRateLimited: false,
  };
}

/**
 * Extrait un message lisible depuis une erreur Axios / réponse API Symfony.
 */
export function apiErrorMessage(err, fallback = 'Une erreur est survenue.') {
  return parseApiError(err, fallback).message;
}
